/**
 * URL-fragment + query transport of the handoff protocol (the App-side
 * receiver for `openInInspector` links).
 *
 * Responsibilities, all on mount (and on `hashchange` so an in-session
 * navigation to an import link also resolves):
 *
 *  1. If `window.location.hash` is an `#import=…` fragment, decode it (gunzip
 *     via the shared permalink codec), hand the message to `parseAndReplace`,
 *     and capture the `sourceId` / `return` envelope. The fragment is then
 *     stripped from the URL (so a reload doesn't re-fire and the gz-b64
 *     payload doesn't linger in the address bar) while the lightweight
 *     `?source=` / `?return=` query markers are written so the inspector keeps
 *     showing the "from <source>" / "Back to <source>" affordances.
 *  2. If there's no `#import=` fragment but the page carries a `?source=`
 *     (and optional `?return=`) query — e.g. a vendor that links with the
 *     fragment AND mirrors the source into the query, or a `?mode=import`
 *     landing — surface the same context so the pre-parse hero can show
 *     "Inspecting message from <source>…" while the user pastes / drops.
 *
 * No network call is issued — gunzip runs through `DecompressionStream`.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { decodeHandoffFragment } from "./protocol";

export interface HandoffContext {
  /** Vendor display id, e.g. "acme-helpdesk". */
  sourceId?: string;
  /** Absolute http(s) URL for the "Back to <source>" link. */
  return?: string;
  /**
   * True once a message decoded from an `#import=` fragment has been handed to
   * the parser. False when the context comes only from a `?source=` query
   * marker (the pre-parse "waiting for a message" state).
   */
  imported: boolean;
}

/** Same URL-safe id alphabet as the protocol's `sourceId`. */
const SOURCE_ID_RE = /^[A-Za-z0-9._-]{1,64}$/;

export function useHandoffImport(options: {
  /** Wire to `email.parseAndReplace`. */
  parseAndReplace: (raw: string) => void;
  /** Fired once if an `#import=` fragment was present but failed to decode. */
  onImportError?: () => void;
}): HandoffContext | null {
  const { parseAndReplace, onImportError } = options;

  const parseRef = useRef(parseAndReplace);
  parseRef.current = parseAndReplace;
  const onErrorRef = useRef(onImportError);
  onErrorRef.current = onImportError;

  const [context, setContext] = useState<HandoffContext | null>(() =>
    typeof window === "undefined" ? null : readQueryContext(),
  );

  const consumedRef = useRef<string | null>(null);

  const tryConsumeFragment = useCallback(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash;
    if (!hash.startsWith("#import=")) {
      // No fragment this pass — fall back to the query-marker context so a
      // `?source=` landing still shows the hint.
      setContext((prev) => prev ?? readQueryContext());
      return;
    }
    // Dedupe: the same fragment must not re-fire across re-renders /
    // duplicate hashchange events.
    if (consumedRef.current === hash) return;
    consumedRef.current = hash;

    void (async () => {
      const decoded = await decodeHandoffFragment(hash);
      if (!decoded) {
        onErrorRef.current?.();
        stripImportFragment(null);
        return;
      }
      parseRef.current(decoded.mime);
      const next: HandoffContext = { imported: true };
      if (decoded.sourceId) next.sourceId = decoded.sourceId;
      if (decoded.return) next.return = decoded.return;
      setContext(next);
      // Replace the heavy fragment with light query markers so the "from
      // <source>" / "Back to <source>" affordances survive a copy of the URL
      // without re-shipping the whole message in the hash.
      stripImportFragment(next);
    })();
  }, []);

  useEffect(() => {
    tryConsumeFragment();
    if (typeof window === "undefined") return;
    window.addEventListener("hashchange", tryConsumeFragment);
    return () => window.removeEventListener("hashchange", tryConsumeFragment);
  }, [tryConsumeFragment]);

  return context;
}

/**
 * Read a `?source=` / `?return=` (or `?mode=import`) query marker into a
 * pre-parse {@link HandoffContext}. Returns `null` when no marker is present.
 */
function readQueryContext(): HandoffContext | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const source = params.get("source");
    const ret = params.get("return");
    const mode = params.get("mode");
    const hasSource = source !== null && SOURCE_ID_RE.test(source);
    const isImportMode = mode === "import";
    if (!hasSource && !isImportMode) return null;
    const ctx: HandoffContext = { imported: false };
    if (hasSource) ctx.sourceId = source;
    if (ret && isHttpUrl(ret)) ctx.return = ret;
    return ctx;
  } catch {
    return null;
  }
}

/**
 * Remove the `#import=` fragment and (when `ctx` is provided) stamp light
 * `?source=` / `?return=` query markers in its place via `replaceState`. When
 * `ctx` is null (decode failure) the fragment is simply cleared.
 */
function stripImportFragment(ctx: HandoffContext | null): void {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    url.hash = "";
    if (ctx?.sourceId) {
      url.searchParams.set("source", ctx.sourceId);
    }
    if (ctx?.return) {
      url.searchParams.set("return", ctx.return);
    }
    window.history.replaceState(null, "", url.toString());
  } catch {
    /* sandboxed / no URL — leave the address bar alone */
  }
}

/** True only for an absolute http(s) URL. */
function isHttpUrl(value: string): boolean {
  try {
    const u = new URL(value);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}
